import React, {Component, useEffect, useRef, useState} from "react";
import "./PumpDetails.css";
import * as columnDefinitionDetails from "../libs/columnDefDetails";
import * as globals from "../libs/globalDef";
import {AgGridReact} from "ag-grid-react";
import * as userLib from "../libs/user-lib";
const API = require("../libs/awsApiWrappers");

/**
 * Copy text to the clipboard, falling back to execCommand where the
 * async Clipboard API is unavailable (e.g. non-HTTPS or older browsers).
 * @param text Text to copy
 * @returns {Promise<void>}
 */
const copyTextToClipboard = text => {
    if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
    }
    return new Promise((resolve, reject) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand("copy") ? resolve() : reject(new Error("Copy failed"));
        } catch (e) {
            reject(e);
        } finally {
            document.body.removeChild(textArea);
        }
    });
};

/**
 * Build a tab-separated string of the row's displayed values
 * (pastes cleanly into Excel / Sheets).
 * @param api ag-Grid api
 * @param columnApi ag-Grid column api (undefined in newer versions)
 * @param node Row node to copy
 * @returns {string}
 */
const rowToTsv = (api, columnApi, node) => {
    // columnApi was merged into api in newer ag-Grid versions
    const columnSource = columnApi || api;
    return columnSource.getAllDisplayedColumns()
        .map(col => {
            const value = api.getValue(col, node);
            return value === null || value === undefined
                ? ""
                : String(value).replace(/[\t\r\n]+/g, " ");
        })
        .join("\t");
};

/**
 * Wraps an AgGridReact and floats a Copy button over the right-hand edge
 * of whichever row the mouse is over (on top of the last visible column).
 * @param getGridApis Function returning {api, columnApi}
 * @param children The AgGridReact element
 */
const RowHoverCopy = ({getGridApis, children}) => {
    const containerRef = useRef(null);
    const timerRef = useRef(null);
    const [hover, setHover] = useState(null); // {rowIndex, top, height, right}
    const [status, setStatus] = useState("idle"); // idle | copied | failed

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const hide = () => {
        setHover(null);
        setStatus("idle");
    };

    const handleMouseOver = event => {
        const container = containerRef.current;
        if (!container || event.target.closest(".row-copy-overlay")) {
            return; // mouse is on the button itself - keep it showing
        }
        const rowEl = event.target.closest(".ag-row");
        const rowIndex = rowEl ? Number(rowEl.getAttribute("row-index")) : NaN;
        if (!rowEl || Number.isNaN(rowIndex)) {
            hide();
            return;
        }
        if (hover && hover.rowIndex === rowIndex) {
            return;
        }

        const containerRect = container.getBoundingClientRect();
        const rowRect = rowEl.getBoundingClientRect();

        // right edge of the visible column area (excludes the vertical scrollbar)
        let right = 0;
        const viewport = container.querySelector(".ag-center-cols-viewport")
            || container.querySelector(".ag-body-viewport");
        if (viewport) {
            const viewportRect = viewport.getBoundingClientRect();
            right = Math.max(0, containerRect.right - (viewportRect.left + viewport.clientWidth));
        }

        setHover({
            rowIndex,
            top: rowRect.top - containerRect.top,
            height: rowRect.height,
            right
        });
        setStatus("idle");
    };

    const handleCopy = event => {
        event.stopPropagation();
        const {api, columnApi} = getGridApis();
        const node = api && hover && api.getDisplayedRowAtIndex(hover.rowIndex);
        if (!node) {
            return;
        }
        copyTextToClipboard(rowToTsv(api, columnApi, node))
            .then(() => setStatus("copied"))
            .catch(() => setStatus("failed"))
            .finally(() => {
                clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => setStatus("idle"), 1500);
            });
    };

    const label = status === "copied" ? "Copied" : status === "failed" ? "Failed" : "Copy";

    return (
        <div
            ref={containerRef}
            className="row-hover-copy"
            onMouseOver={handleMouseOver}
            onMouseLeave={hide}
            onScrollCapture={hide}
        >
            {children}
            {hover &&
            <div
                className="row-copy-overlay"
                style={{top: hover.top, height: hover.height, right: hover.right}}
            >
                <button
                    type="button"
                    className={"copy-row-button" + (status === "copied" ? " copied" : "")}
                    title="Copy row to clipboard"
                    onClick={handleCopy}
                >
                    {label}
                </button>
            </div>}
        </div>
    );
};


/**
 * Displays pump info details
 */
export default class PumpDetails extends Component {
    constructor(props) {
        super(props);

        const columnDefs = JSON.parse(JSON.stringify(columnDefinitionDetails.columnDefsDetails));
        // disable tick box
        columnDefs[0].checkboxSelection = false;

        this.state = {
            isLoading: null,
            isDeleting: null,
            pump: null,

            columnDefs: columnDefs,
            columnDefsLogs: columnDefinitionDetails.columnDefsLogs,

            defaultColDef: {
                resizable: true
            },

            style: {
                width: '100%',
                // height: '364px'
                // height: '84px'
                height: '110px'
            },

            styleLogs: {
                width: 4 * globals.standardColWidthLogs + 20,
                // height: '364px'
                // height: '84px'
                // height: '100%'
                height: '550px'
            },

            isAdminEditor: userLib.isAdminEditor(this.props.groups),
            isAdmin: userLib.isAdminEditor(this.props.groups) || userLib.isAdminViewer(this.props.groups),

            rowData: [],
            rowDataLogs: [],
        };
    }

    onGridReady = params => {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;

        const updateData = data => {
            this.setState({rowData: data});
        };

        updateData(this.state.rowData);
    };

    onGridReadyLogs = params => {
        this.gridApiLogs = params.api;
        // this.gridColumnApiLogs = params.columnApi;

        const updateData = data => {
            this.setState({rowDataLogs: data});
        };

        updateData(this.state.rowDataLogs);

    };

    getPumpGridApis = () => ({api: this.gridApi, columnApi: this.gridColumnApi});

    componentDidUpdate() {
        if (this.gridApi) {
            this.gridApi.refreshCells();
        }

        if (this.gridApiLogs) {
            this.gridApiLogs.refreshCells();
        }
    }

    /**
     * Format log records for display in log table
     * @param logRecords Log records to display
     * @returns {Array} Formatted data to be shown in the grid
     */
    processLogsRecords = logRecords => {
        const dataForTable = [];
        // collect up to 50 timestamps
        const maxEntries = 51;
        let i;
        let counter = 0;
        let previousTime = "";
        let oneRow = {};
        let hasElement = false;
        for (i = logRecords.length - 1; (i >= 0) && (counter <= (maxEntries * 3)); i--) {
            counter++;
            const record = logRecords[i].Key;
            if (record) {
                let timeRecord = record.substring(
                    record.lastIndexOf("/") + 1,
                    record.lastIndexOf("___"));

                timeRecord = this.adjustDateField(timeRecord);

                if (timeRecord !== previousTime) {
                    if (previousTime !== "") {
                        // this is a new complete record
                        dataForTable.push(oneRow);
                    }
                    previousTime = timeRecord;
                    oneRow = {[globals.dateTime]: timeRecord};
                    hasElement = false;
                }
                if (record.indexOf('cad') !== -1) {
                    oneRow[globals.cadLog] = record;
                    hasElement = true;
                } else if (record.indexOf('debug') !== -1) {
                    oneRow[globals.debugLog] = record;
                    hasElement = true;
                } else if (record.indexOf('room') !== -1) {
                    oneRow[globals.database] = record;
                    hasElement = true;
                }
            }
        }
        // push last element
        if (hasElement) {
            dataForTable.push(oneRow);
        }
        return dataForTable;
    };

    /**
     * Format date information
     * @param records Records to process
     * @returns {Array} Reformatted records
     */
    processRecords = records => {
        let record;
        for (record of records) {
            try {
                if (record[globals.dateClStarted]) {
                    record[globals.dateClStarted] =
                        this.adjustDate(record[globals.dateClStarted]);
                }
                if (record[globals.dateUsed]) {
                    record[globals.dateUsed] = this.adjustDate(record[globals.dateUsed]);
                }
            } catch (e) {
                // do nothing
            }
        }
        return records;
    };

    /**
     * Reformat date to limit confusion
     * @param date Date to reformat
     * @returns {*} Reformatted date
     */
    adjustDate = date => {
        if (date) {
            return date.replace(/T/g, '  ').slice(0, -8);
        } else {
            return date;
        }
    };

    replaceStringAt(str, index, replacement) {
        return str.substr(0, index) + replacement + str.substr(index + replacement.length);
    }

    adjustDateField = date => {
        if (date) {
            date = date.replace(/__/g, '  ').replace(/_/g, '-');
            const desiredLength = 17;
            if (date.length > desiredLength) {
                date = date.substring(date.length - desiredLength);
            }
            return this.replaceStringAt(date, 14, ':');
        } else {
            return date;
        }
    };

    async componentDidMount() {
        try {
            this.setState({
                distributorId:
                    userLib.getCustomAttribute(this.props.currentUser, 'distributorId')
            });
            if (!this.props.pumpToDuplicate) {
                const pump = await this.getPump();
                this.props.setPumpToDuplicate([pump]);
            }
            const pump = await this.getPumpDetails();
            const records = this.processRecords(pump.recordItems);
            this.setState({rowData: records});
            const logRecords = this.processLogsRecords(pump.logs);
            this.setState({rowDataLogs: logRecords});
        } catch (e) {
            API.handleApiErrorWithAlert(e, this)
        }
        this.props.setUpdateTimeOutListener();
    }

    getPump() {
        return API.get("pumps", `/pumps/${this.props.match.params.id}`, {});
    }

    getPumpDetails() {
        let myInit = {
            queryStringParameters: {isCommercial: this.props.pumpToDuplicate[0].commercial}
        };
        return API.get("pumps", `/pumps/info/${this.props.match.params.id}`, myInit);
    }

    render() {
        return (
            <div className="PumpDetails">
                {this.state.rowData &&
                <div
                    id="pumpGrid"
                    style={this.state.style}
                    className="ag-theme-balham"
                >
                    <RowHoverCopy getGridApis={this.getPumpGridApis}>
                        <AgGridReact
                            columnDefs={this.state.columnDefs}
                            defaultColDef={this.state.defaultColDef}
                            onGridReady={this.onGridReady}
                            enableCellTextSelection={true}
                            // singleClickEdit={true}
                            // components={this.state.components}
                            rowData={this.state.rowData}
                        />
                    </RowHoverCopy>
                </div>}
                {this.state.rowDataLogs
                && (this.state.isAdmin
                    || (this.state.distributorId === '70000')
                        || (this.state.distributorId === '84412230058')
                        || (this.state.distributorId === '700008888')) // TODO: remove after penetration testing
                && <div
                    id="pumpGridLogs"
                    style={this.state.styleLogs}
                    className="ag-theme-balham log-grid"
                >
                    <AgGridReact
                        columnDefs={this.state.columnDefsLogs}
                        defaultColDef={this.state.defaultColDef}
                        onGridReady={this.onGridReadyLogs}
                        enableCellTextSelection={true}
                        // singleClickEdit={true}
                        // components={this.state.components}
                        /* this is where we provide custom components */
                        // frameworkComponents={this.state.referenceFormatter}
                        rowData={this.state.rowDataLogs}
                    />
                </div>
                }
            </div>
        );
    }
}